import React, { useState, useEffect, useCallback } from 'react';
import {
    AlertTriangle,
    RefreshCw,
    Bot,
    ChevronRight,
    Sparkles,
    FileText,
    BarChart3,
    CalendarClock,
    Users,
    ShieldCheck,
    FileCheck,
    HelpCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient, ApiError, DashboardStats, DriverDue, AuthResponse } from '../services/api';
import { useScoringConfig } from '../contexts/ScoringConfigContext';
import SiteRiskWidget from './SitesWidget';
import Footer from './Footer';
import RefreshErrorBanner from './RefreshErrorBanner';

type User = AuthResponse['user'];

interface DashboardProps {
    user: User | null;
}

const BRAND_GREEN = '#69BE28';

const Dashboard: React.FC<DashboardProps> = ({ user }) => {
    const navigate = useNavigate();
    const { lowMediumBoundary, mediumHighBoundary } = useScoringConfig();

    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [driversDue, setDriversDue] = useState<DriverDue[]>([]);
    const [automationSettings, setAutomationSettings] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const [statsRes, dueRes] = await Promise.all([
                apiClient.getDashboardStats(),
                apiClient.getDriversDue()
            ]);

            setStats(statsRes);
            setDriversDue(dueRes);

            if (user?.organisation?.automatedChecks) {
                try {
                    const settings = await apiClient.getAutomationSettings();
                    setAutomationSettings(settings);
                } catch (err) {
                    console.error('Failed to load automation settings:', err);
                }
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err : new Error('Failed to load dashboard data'));
        } finally {
            setIsLoading(false);
        }
    }, [user?.organisation?.automatedChecks]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleRefresh = useCallback(() => {
        setRefreshKey(k => k + 1);
        loadData();
    }, [loadData]);

    const getRiskBarClass = (riskLevel: 'low' | 'medium' | 'high' | null): string => {
        switch (riskLevel) {
            case 'low':    return 'bg-green-500';
            case 'medium': return 'bg-amber-400';
            case 'high':   return 'bg-red-500';
            default:       return 'bg-gray-300';
        }
    };

    const getInitials = (firstName: string, lastName: string) =>
        `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

    const rateLimited = error instanceof ApiError && error.status === 429;
    const refreshErrorMessage = rateLimited
        ? `Updates paused.${error.retryAfterSeconds ? ` Try again in ${error.retryAfterSeconds} seconds.` : ' Please try again shortly.'}`
        : "We couldn't refresh the dashboard.";


    if (isLoading && !stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" style={{ color: BRAND_GREEN }} />
                <span className="text-gray-600">Loading dashboard...</span>
            </div>
        );
    }

    if (error && !stats) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                    <p className="text-gray-700 mb-4">{rateLimited ? refreshErrorMessage : error.message}</p>
                    <button
                        onClick={loadData}
                        className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: BRAND_GREEN }}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!stats) return null;

    // ---- Derived figures ----
    const clearTotal = stats.clearToDrive + stats.notClearToDrive;
    const compliancePct = clearTotal > 0 ? Math.round((stats.clearToDrive / clearTotal) * 100) : null;

    const highRisk = stats.riskLevels.high;
    const checksDue = driversDue.length;
    const pendingConsents = stats.driversWithoutConsent;
    const mostOverdue = driversDue.length > 0
        ? [...driversDue].sort((a, b) => b.daysOverdue - a.daysOverdue)[0]
        : null;

    // ---- Briefing: 4 fixed cards, always shown ----
    interface BriefingCard {
        status: 'ok' | 'warn' | 'critical';
        badge: string;
        title: string;
        detail: string;
        actionLabel?: string;
        onClick: () => void;
    }

    const briefingCards: BriefingCard[] = [
        // 1. Clear to drive
        stats.notClearToDrive > 0
            ? {
                status: 'critical',
                badge: String(stats.notClearToDrive),
                title: `${stats.notClearToDrive} driver${stats.notClearToDrive !== 1 ? 's' : ''} not clear to drive`,
                detail: 'These drivers should not be behind the wheel until their status is resolved.',
                actionLabel: 'View drivers',
                onClick: () => navigate('/drivers?clearToDrive=false'),
            }
            : {
                status: 'ok',
                badge: '✓',
                title: clearTotal > 0 ? 'All drivers clear to drive' : 'No checked drivers yet',
                detail: clearTotal > 0
                    ? `${clearTotal} driver${clearTotal !== 1 ? 's' : ''} checked and clear.`
                    : 'Run a licence check to get started.',
                actionLabel: clearTotal === 0 ? 'Go to Drivers' : undefined,
                onClick: () => navigate('/drivers'),
            },

        // 2. High risk
        highRisk > 0
            ? {
                status: 'critical',
                badge: String(highRisk),
                title: `${highRisk} high-risk driver${highRisk !== 1 ? 's' : ''} need review`,
                detail: 'These drivers need immediate attention based on their latest risk score.',
                actionLabel: 'View high-risk drivers',
                onClick: () => navigate('/drivers?risk=high'),
            }
            : {
                status: 'ok',
                badge: '✓',
                title: 'No high-risk drivers',
                detail: stats.riskLevels.medium > 0
                    ? `${stats.riskLevels.medium} driver${stats.riskLevels.medium !== 1 ? 's' : ''} at medium risk.`
                    : 'All checked drivers are low risk.',
                onClick: () => navigate('/drivers'),
            },

        // 3. Checks overdue
        mostOverdue
            ? {
                status: 'warn',
                badge: String(checksDue),
                title: `${mostOverdue.firstName} ${mostOverdue.lastName} is ${mostOverdue.daysOverdue} day${mostOverdue.daysOverdue !== 1 ? 's' : ''} overdue`,
                detail: checksDue > 1
                    ? `${checksDue} drivers are due a licence check in total.`
                    : 'The only driver currently due a licence check.',
                actionLabel: 'See drivers due',
                onClick: () => navigate('/drivers?filter=due'),
            }
            : {
                status: 'ok',
                badge: '✓',
                title: 'All checks up to date',
                detail: 'No drivers are currently overdue for a licence check.',
                onClick: () => navigate('/drivers'),
            },

        // 4. DBS (only when add-on is enabled for this org)
        ...(user?.organisation?.trustidEnabled ? [
            stats.dbsActionRequired > 0
                ? {
                    status: 'warn' as const,
                    badge: String(stats.dbsActionRequired),
                    title: `${stats.dbsActionRequired} DBS check${stats.dbsActionRequired !== 1 ? 's' : ''} require action`,
                    detail: 'Identity verified; waiting for you to initiate the DBS application.',
                    actionLabel: 'View drivers',
                    onClick: () => navigate('/drivers'),
                }
                : {
                    status: 'ok' as const,
                    badge: '✓',
                    title: 'No DBS action required',
                    detail: stats.dbsInProgress > 0
                        ? `${stats.dbsInProgress} check${stats.dbsInProgress !== 1 ? 's' : ''} in progress.`
                        : 'No active DBS checks.',
                    onClick: () => navigate('/drivers'),
                },
        ] : []),
    ];

    const issueCount = briefingCards.filter(c => c.status !== 'ok').length;

    // "Also:" items - minor expiry warnings that don't warrant a full card
    const alsoItems: { label: string; onClick: () => void }[] = [];
    if (stats.expiringLicence > 0) alsoItems.push({
        label: `${stats.expiringLicence} licence${stats.expiringLicence !== 1 ? 's' : ''} expiring soon`,
        onClick: () => navigate('/drivers?show=licence_expiring&maxDays=30'),
    });
    if (pendingConsents > 0) alsoItems.push({
        label: `${pendingConsents} consent${pendingConsents !== 1 ? 's' : ''} outstanding`,
        onClick: () => navigate('/drivers?show=no_consent'),
    });
    if (stats.consentExpiring > 0) alsoItems.push({
        label: `${stats.consentExpiring} consent${stats.consentExpiring !== 1 ? 's' : ''} expiring soon`,
        onClick: () => navigate('/drivers?show=consent_expiring&maxDays=30'),
    });

    const badgeStyles: Record<BriefingCard['status'], string> = {
        critical: 'bg-red-500 text-white',
        warn: 'bg-amber-400 text-amber-950',
        ok: 'bg-[#69BE28] text-white',
    };

    const riskRows: { level: 'low' | 'medium' | 'high'; label: string; count: number; color: string }[] = [
        { level: 'low', label: 'Low', count: stats.riskLevels.low, color: BRAND_GREEN },
        { level: 'medium', label: 'Medium', count: stats.riskLevels.medium, color: '#f59e0b' },
        { level: 'high', label: 'High', count: stats.riskLevels.high, color: '#ef4444' }
    ];

    return (
        <div className="min-h-full" style={{ backgroundColor: '#f3f5f1' }}>
        <div className="max-w-7xl mx-auto py-5 px-4 sm:px-6 lg:px-8 space-y-5">




            {error && (
                <RefreshErrorBanner
                    message={refreshErrorMessage}
                    rateLimited={rateLimited}
                    onRetry={handleRefresh}
                />
            )}

            {/* ===== Today's briefing - the single home for anything actionable ===== */}
            <div
                className="rounded-2xl p-6 text-white relative overflow-hidden"
                style={{ background: 'linear-gradient(115deg, #1c2417 0%, #26361a 55%, #2f4520 100%)' }}
            >
                <div
                    className="absolute -top-32 -right-16 w-96 h-96 rounded-full pointer-events-none"
                    style={{ background: 'radial-gradient(circle, rgba(105,190,40,0.25), transparent 65%)' }}
                />
                <div className="relative">
                    <div className="flex items-center justify-between">
                        <span
                            className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full"
                            style={{ backgroundColor: 'rgba(105,190,40,0.18)', border: '1px solid rgba(105,190,40,0.4)', color: '#b9e88f' }}
                        >
                            <Sparkles className="w-3 h-3" />
                            Today's briefing
                        </span>
                        <button
                            onClick={handleRefresh}
                            title="Refresh dashboard"
                            className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
                        >
                            <RefreshCw className={`w-4 h-4 text-white/70 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                    <h2 className="text-xl font-bold mt-3">
                        {compliancePct !== null
                            ? `Your fleet is ${compliancePct}% compliant.`
                            : 'Welcome to your fleet overview.'}
                        {issueCount > 0
                            ? ` ${issueCount} thing${issueCount !== 1 ? 's' : ''} need${issueCount === 1 ? 's' : ''} your attention.`
                            : ' Nothing needs your attention today.'}
                    </h2>
                    <p className="text-sm mt-1.5" style={{ color: '#c6d4bc' }}>
                        {stats.checksToday > 0
                            ? `${stats.checksToday} licence check${stats.checksToday !== 1 ? 's' : ''} completed today · ${stats.checksThisMonth} this month.`
                            : `${stats.checksThisMonth} licence check${stats.checksThisMonth !== 1 ? 's' : ''} completed this month.`}
                        {automationSettings?.enabled && ' Automated checking is active.'}
                    </p>

                    <div className={`grid gap-3 mt-4`} style={{ gridTemplateColumns: `repeat(${briefingCards.length}, minmax(0, 1fr))` }}>
                        {briefingCards.map((card, i) => (
                            <button
                                key={i}
                                onClick={card.onClick}
                                className="text-left rounded-xl p-3 transition-colors hover:bg-white/15 flex flex-col"
                                style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}
                            >
                                <div className="flex items-start gap-2.5">
                                    <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${badgeStyles[card.status]}`}>
                                        {card.badge}
                                    </span>
                                    <p className="text-sm font-semibold leading-snug">{card.title}</p>
                                </div>
                                <p className="text-xs mt-2 leading-relaxed flex-1" style={{ color: '#aebba5' }}>{card.detail}</p>
                                {card.actionLabel && (
                                    <span className="text-xs font-semibold mt-2" style={{ color: '#b9e88f' }}>
                                        {card.actionLabel} →
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {alsoItems.length > 0 && (
                        <p className="text-xs mt-4" style={{ color: '#aebba5' }}>
                            Also:{' '}
                            {alsoItems.map((item, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && ' · '}
                                    <button
                                        onClick={item.onClick}
                                        className="underline underline-offset-2 hover:opacity-80 transition-opacity"
                                        style={{ color: '#c6d4bc' }}
                                    >
                                        {item.label}
                                    </button>
                                </React.Fragment>
                            ))}
                        </p>
                    )}
                </div>
            </div>

            {/* ===== Fleet state - neutral health gauges, no tasks ===== */}
            <div className={`grid grid-cols-2 gap-4 ${user?.organisation?.trustidEnabled ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`}>
                <button
                    onClick={() => navigate('/drivers')}
                    className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-5 text-left hover:shadow-lg transition-shadow flex flex-col"
                >
                    <div className="p-2 rounded-lg mb-3 w-fit" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <Users className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                    </div>
                    <p className="text-sm font-medium text-gray-500">Total drivers</p>
                    <p className="text-4xl font-bold text-gray-900 mt-2">{stats.totalDrivers}</p>
                    <p className="text-xs text-gray-400 mt-auto pt-2">{stats.activeDrivers} active</p>
                </button>
                <button
                    onClick={() => navigate('/drivers?clearToDrive=false')}
                    className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-5 text-left hover:shadow-lg transition-shadow flex flex-col"
                >
                    <div className="p-2 rounded-lg mb-3 w-fit" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <ShieldCheck className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                    </div>
                    <p className="text-sm font-medium text-gray-500">Clear to drive</p>
                    <p className="text-4xl font-bold mt-2" style={{ color: stats.notClearToDrive === 0 && clearTotal > 0 ? '#3d7a12' : '#111827' }}>
                        {clearTotal > 0 ? (
                            <>
                                {stats.clearToDrive}
                                <span className="text-xl text-gray-400">/{clearTotal}</span>
                            </>
                        ) : (
                            <span className="text-xl text-gray-500">N/A</span>
                        )}
                    </p>
                    {clearTotal > 0 && (
                        <div className="mt-3 h-1 rounded-full bg-gray-100 overflow-hidden">
                            <div
                                className="h-full rounded-full"
                                style={{ width: `${Math.round((stats.clearToDrive / clearTotal) * 100)}%`, backgroundColor: BRAND_GREEN }}
                            />
                        </div>
                    )}
                    <p className="text-xs text-gray-400 mt-auto pt-2">
                        {clearTotal === 0 ? 'No checked drivers yet' : stats.notClearToDrive === 0 ? 'All checked drivers clear' : `${stats.notClearToDrive} not clear`}
                    </p>
                </button>
                <button
                    onClick={() => navigate('/drivers?show=no_consent')}
                    className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-5 text-left hover:shadow-lg transition-shadow flex flex-col"
                >
                    <div className="p-2 rounded-lg mb-3 w-fit" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <FileCheck className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                    </div>
                    <p className="text-sm font-medium text-gray-500">Consented</p>
                    <p className="text-4xl font-bold mt-2" style={{ color: stats.driversWithConsent === stats.activeDrivers && stats.activeDrivers > 0 ? '#3d7a12' : '#111827' }}>
                        {stats.activeDrivers > 0 ? (
                            <>
                                {stats.driversWithConsent}
                                <span className="text-xl text-gray-400">/{stats.activeDrivers}</span>
                            </>
                        ) : (
                            <span className="text-xl text-gray-500">N/A</span>
                        )}
                    </p>
                    {stats.activeDrivers > 0 && (
                        <div className="mt-3 h-1 rounded-full bg-gray-100 overflow-hidden">
                            <div
                                className="h-full rounded-full"
                                style={{ width: `${Math.round((stats.driversWithConsent / stats.activeDrivers) * 100)}%`, backgroundColor: BRAND_GREEN }}
                            />
                        </div>
                    )}
                    <p className="text-xs text-gray-400 mt-auto pt-2">
                        {stats.activeDrivers > 0
                            ? stats.driversWithoutConsent === 0 ? 'All drivers consented' : `${stats.driversWithoutConsent} without consent`
                            : 'No active drivers yet'}
                    </p>
                </button>
                {user?.organisation?.trustidEnabled && (
                    <button
                        onClick={() => navigate(stats.dbsActionRequired > 0 ? '/drivers?dbs=action_required' : '/drivers?dbs=in_progress')}
                        className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-5 text-left hover:shadow-lg transition-shadow flex flex-col"
                    >
                        <div className="p-2 rounded-lg mb-3 w-fit" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                            <FileText className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                        </div>
                        <p className="text-sm font-medium text-gray-500">DBS checks</p>
                        <p className="text-4xl font-bold text-gray-900 mt-2">{stats.dbsInProgress}</p>
                        <p className="text-xs mt-auto pt-2" style={{
                            color: stats.dbsActionRequired > 0 ? '#dc2626' : '#9ca3af'
                        }}>
                            {stats.dbsActionRequired > 0
                                ? `${stats.dbsActionRequired} require action`
                                : stats.dbsInProgress > 0
                                    ? 'All progressing'
                                    : 'No active checks'}
                        </p>
                    </button>
                )}
                <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-5 flex flex-col">
                    <div className="p-2 rounded-lg mb-3 w-fit" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                        <BarChart3 className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                    </div>
                    <p className="text-sm font-medium text-gray-500">Checks this month</p>
                    {user?.organisation?.trustidEnabled ? (
                        <div className="flex gap-2 mt-2">
                            <div className="flex-1 bg-gray-50 rounded-xl px-3 py-1.5 border border-gray-100">
                                <p className="text-xs font-medium text-gray-500">Licence</p>
                                <p className="text-3xl font-bold text-gray-900 mt-0.5">{stats.checksThisMonth}</p>
                            </div>
                            <div className="flex-1 bg-gray-50 rounded-xl px-3 py-1.5 border border-gray-100">
                                <p className="text-xs font-medium text-gray-500">DBS</p>
                                <p className="text-3xl font-bold text-gray-900 mt-0.5">{stats.dbsChecksThisMonth}</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-4xl font-bold text-gray-900 mt-2">{stats.checksThisMonth}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-auto pt-2">{stats.checksToday} completed today</p>
                </div>
            </div>

            {/* ===== Risk distribution + Automated checks ===== */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                                <BarChart3 className="w-4 h-4" style={{ color: BRAND_GREEN }} />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900">Risk distribution</h3>
                            <div className="relative group">
                                <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                                <div className="absolute left-0 top-6 w-72 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                                    <p className="font-semibold text-sm mb-2">Risk distribution</p>
                                    <p className="text-gray-300 mb-2">Shows how your drivers are spread across three risk bands based on their licence score.</p>
                                    <ul className="text-gray-300 space-y-1">
                                        <li><span className="text-green-400 font-medium">Low</span> - score below {lowMediumBoundary}</li>
                                        <li><span className="text-yellow-400 font-medium">Medium</span> - score {lowMediumBoundary}-{mediumHighBoundary}</li>
                                        <li><span className="text-red-400 font-medium">High</span> - score {mediumHighBoundary} or above</li>
                                    </ul>
                                    <div className="absolute -top-1.5 left-3 w-3 h-3 bg-gray-900 rotate-45"></div>
                                </div>
                            </div>
                        </div>
                        <span className="text-sm text-gray-500">
                            Average score <span className="font-semibold text-gray-900">{stats.averageRiskScore}</span>
                        </span>
                    </div>
                    <div className="space-y-3">
                        {riskRows.map((row) => {
                            const pct = stats.totalDrivers > 0 ? (row.count / stats.totalDrivers) * 100 : 0;
                            return (
                                <button
                                    key={row.level}
                                    onClick={() => navigate(`/drivers?risk=${row.level}`)}
                                    className="w-full flex items-center gap-4 rounded-xl border border-gray-100 px-4 py-3 hover:bg-gray-50 transition-colors group"
                                >
                                    <span className="text-sm font-medium text-gray-700 w-20 text-left">{row.label}</span>
                                    <div className="flex-1 bg-gray-100 rounded-full h-3">
                                        <div
                                            className="h-3 rounded-full transition-all"
                                            style={{ width: `${pct}%`, backgroundColor: row.count > 0 ? row.color : '#d1d5db' }}
                                        />
                                    </div>
                                    <span className="text-sm font-semibold text-gray-900 w-10 text-right">{row.count}</span>
                                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                                </button>
                            );
                        })}
                    </div>
                </div>

                {user?.organisation?.automatedChecks ? (
                    <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6 flex flex-col">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                                    <Bot className="w-5 h-5" style={{ color: BRAND_GREEN }} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-semibold text-gray-900">Automated checks</h3>
                                        <div className="relative group">
                                            <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                                            <div className="absolute left-0 top-6 w-72 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                                                <p className="font-semibold text-sm mb-2">Automated checks</p>
                                                <p className="text-gray-300 mb-2">DVLA licence checks run automatically on a schedule so you don't need to trigger them manually.</p>
                                                <p className="text-gray-300">When active, each driver's licence is checked at the configured frequency and results are updated instantly.</p>
                                                <div className="absolute -top-1.5 left-3 w-3 h-3 bg-gray-900 rotate-45"></div>
                                            </div>
                                        </div>
                                    </div>
                                    {automationSettings?.autoCheckOnConsent && (
                                        <p className="text-xs text-gray-500">Runs when a driver approves consent</p>
                                    )}
                                </div>
                            </div>
                            {automationSettings?.enabled ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                    </span>
                                    Active
                                </span>
                            ) : (
                                <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                    {automationSettings ? 'Inactive' : '...'}
                                </span>
                            )}
                        </div>
                        <div className="space-y-4 mt-5">
                            <div>
                                <p className="text-xs text-gray-400">Next run</p>
                                <p className="text-sm font-semibold text-gray-900 mt-1">
                                    {automationSettings?.enabled && automationSettings?.nextScheduledRun
                                        ? new Date(automationSettings.nextScheduledRun).toLocaleString('en-GB', {
                                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                                        })
                                        : automationSettings?.enabled
                                            ? `Today at ${automationSettings?.preferredCheckTime || '03:00'}`
                                            : 'Not scheduled'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400">Last run</p>
                                <p className="text-sm font-semibold text-gray-900 mt-1">
                                    {automationSettings?.lastRunDriverCount !== null && automationSettings?.lastRunDriverCount !== undefined
                                        ? `${automationSettings.lastRunDriverCount} driver${automationSettings.lastRunDriverCount !== 1 ? 's' : ''} checked`
                                        : 'No runs yet'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => navigate('/settings?tab=automation')}
                            className="mt-auto pt-5 text-sm font-medium text-left hover:opacity-80 transition-opacity"
                            style={{ color: BRAND_GREEN }}
                        >
                            Configure →
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)] p-6 flex flex-col">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-xl" style={{ backgroundColor: 'rgba(105,190,40,0.12)' }}>
                                    <Bot className="w-5 h-5" style={{ color: BRAND_GREEN }} />
                                </div>
                                <h3 className="text-base font-semibold text-gray-900">Automated checks</h3>
                            </div>
                            <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                                Inactive
                            </span>
                        </div>
                        <p className="text-sm text-gray-400 mt-4">
                            This add-on enables automatic licence checks so you never need to trigger them manually.
                        </p>
                        <div className="mt-auto pt-5 space-y-2">
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Includes</p>
                            <div className="flex flex-col gap-1.5">
                                <span className="text-xs text-gray-500 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                                    Scheduled checks by risk level
                                </span>
                                <span className="text-xs text-gray-500 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                                    Automatic check on consent approval
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ===== Site risk overview ===== */}
            <SiteRiskWidget onViewAll={() => navigate('/sites')} refreshKey={refreshKey} />

            {/* ===== Drivers due for checks - the work queue, full width ===== */}
            {driversDue.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200/80 shadow-[0_1px_3px_rgba(15,25,10,0.06),0_4px_14px_rgba(15,25,10,0.05)]">
                    <div className="flex items-center justify-between px-6 pt-5 pb-4">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-amber-100">
                                <CalendarClock className="w-4 h-4 text-amber-600" />
                            </div>
                            <h3 className="text-base font-semibold text-gray-900">Drivers Due for Licence Checks</h3>
                            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">{driversDue.length}</span>
                        </div>
                        <button onClick={() => navigate('/drivers?filter=due')} className="text-sm font-medium hover:opacity-80 transition-opacity" style={{ color: BRAND_GREEN }}>
                            View all →
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-100">
                            <thead className="border-b border-gray-100 bg-gray-50/95 backdrop-blur-sm">
                                <tr>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Driver</th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Site</th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Risk</th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Overdue</th>
                                    <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Reason</th>
                                    <th className="px-4 py-3.5"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {[...driversDue].sort((a, b) => b.daysOverdue - a.daysOverdue).slice(0, 5).map((driver) => (
                                    <tr key={driver.id} className="hover:bg-gray-50/60 transition-colors">
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 bg-gray-100 text-gray-600">
                                                    {getInitials(driver.firstName, driver.lastName)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900">{driver.firstName} {driver.lastName}</p>
                                                    <p className="text-xs text-gray-400">{driver.licenceNumber}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3.5 text-sm text-gray-600">
                                            {driver.siteName ?? <span className="text-base leading-none text-gray-300">-</span>}
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-semibold text-gray-700">
                                                <span className={`h-4 w-[3px] rounded-full ${getRiskBarClass(driver.riskLevel)}`} aria-hidden="true" />
                                                {driver.riskLevel ? driver.riskLevel.charAt(0).toUpperCase() + driver.riskLevel.slice(1) : 'Unknown'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3.5">
                                            <span className="text-sm font-semibold text-red-600">{driver.daysOverdue} days</span>
                                        </td>
                                        <td className="px-4 py-3.5 text-sm text-gray-600">{driver.reason}</td>
                                        <td className="px-6 py-3.5 text-right">
                                            <button onClick={() => navigate(`/drivers?id=${driver.id}&tab=licence`)} className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors" style={{ backgroundColor: 'rgba(105,190,40,0.12)', color: '#3d7a12' }}>
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {driversDue.length > 5 && (
                        <div className="px-6 py-3 text-center border-t border-gray-50">
                            <button onClick={() => navigate('/drivers?filter=due')} className="text-sm font-medium hover:underline" style={{ color: BRAND_GREEN }}>
                                View {driversDue.length - 5} more drivers →
                            </button>
                        </div>
                    )}
                </div>
            )}

        </div>
        <Footer />
        </div>
    );
};

export default Dashboard;
